import React from 'react';
import { Award, GraduationCap, ListChecks } from 'lucide-react';
import InlineEmpty from '../InlineEmpty/InlineEmpty';
import StatusBadge from '../StatusBadge/StatusBadge';
import { formatDate } from './time';
import { useI18n } from '../../i18n/I18nContext';
import './Student360.css';

const EXAM_TONE = { IN_PROGRESS: 'info', SUBMITTED: 'warning', GRADED: 'neutral', EXPIRED: 'danger' };
const CERTIFICATE_TONE = { ISSUED: 'success', REVOKED: 'danger' };

/** One list of results: a heading with its count, then the rows (or one calm line when there are none). */
function Group({ title, count, children }) {
  return (
    <div className="s360-group">
      <div className="s360-group-head">
        <h2 className="s360-group-title">{title}</h2>
        {count > 0 && <span className="ui-count">{count}</span>}
      </div>
      {children}
    </div>
  );
}

/**
 * The results facet: the latest attempt of every quiz (a quiz counts as passed when the attempt is
 * "completed"), every exam attempt and the certificates, one list each. A row carries the title and
 * its course on the left and one score, one badge and the date on the right. Only courses inside the
 * viewer's reach come back, so a title shown here is always one this viewer may know.
 */
function ResultsSection({ results }) {
  const { t, lang } = useI18n();
  const quizzes = results.quizzes || [];
  const exams = results.exams || [];
  const certificates = results.certificates || [];

  return (
    <section className="section-card s360-section" aria-label={t('student360.results.title')}>
      <Group title={t('student360.results.quizzes')} count={quizzes.length}>
        {quizzes.length === 0 ? (
          <InlineEmpty icon={<ListChecks />} title={t('student360.results.noQuizzes')} />
        ) : (
          <ul className="s360-list">
            {quizzes.map((quiz) => (
              <li key={quiz.attemptId} className="s360-item">
                <div className="s360-item__main">
                  <span className="s360-item__title" title={quiz.title}>{quiz.title}</span>
                  <span className="s360-item__sub">
                    {[quiz.courseTitle, quiz.attempts > 1 ? t('student360.results.attempts', { count: quiz.attempts }) : '']
                      .filter(Boolean).join(' · ')}
                  </span>
                </div>
                <div className="s360-item__side">
                  <span className={`s360-score ${quiz.passed ? 'is-pass' : 'is-low'}`}>
                    {`${quiz.score}/${quiz.totalScore}`}
                  </span>
                  <StatusBadge tone={quiz.passed ? 'success' : 'danger'}>
                    {t(quiz.passed ? 'student360.results.passed' : 'student360.results.failed')}
                  </StatusBadge>
                  <span className="s360-item__date">{formatDate(quiz.submittedAt, lang)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Group>

      <Group title={t('student360.results.exams')} count={exams.length}>
        {exams.length === 0 ? (
          <InlineEmpty icon={<GraduationCap />} title={t('student360.results.noExams')} />
        ) : (
          <ul className="s360-list">
            {exams.map((exam) => {
              const graded = exam.isPassed !== null && exam.isPassed !== undefined;
              return (
                <li key={exam.attemptId} className="s360-item">
                  <div className="s360-item__main">
                    <span className="s360-item__title" title={exam.title}>{exam.title}</span>
                    {exam.courseTitle && <span className="s360-item__sub">{exam.courseTitle}</span>}
                    {exam.leaveCount > 0 && (
                      <span className="s360-item__sub">
                        <StatusBadge tone={exam.endedForLeaving ? 'danger' : 'warning'}>
                          {exam.endedForLeaving
                            ? t('student360.results.endedForLeaving', { count: exam.leaveCount })
                            : t('student360.results.leftExam', { count: exam.leaveCount })}
                        </StatusBadge>
                      </span>
                    )}
                  </div>
                  <div className="s360-item__side">
                    {exam.score !== null && exam.score !== undefined && (
                      <span className={`s360-score ${exam.isPassed ? 'is-pass' : 'is-low'}`}>
                        {`${exam.score}/${exam.maxScore}`}
                        {exam.percentage !== null && exam.percentage !== undefined ? ` · ${exam.percentage}%` : ''}
                      </span>
                    )}
                    {/* A graded attempt says passed or not; the status is only worth a badge while it is open */}
                    {graded && exam.status === 'GRADED' ? (
                      <StatusBadge tone={exam.isPassed ? 'success' : 'danger'}>
                        {t(exam.isPassed ? 'student360.results.passed' : 'student360.results.failed')}
                      </StatusBadge>
                    ) : (
                      <StatusBadge tone={EXAM_TONE[exam.status] || 'neutral'}>
                        {t(`student360.results.examStatus.${exam.status}`)}
                      </StatusBadge>
                    )}
                    <span className="s360-item__date">{formatDate(exam.submittedAt || exam.startedAt, lang)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Group>

      <Group title={t('student360.results.certificates')} count={certificates.length}>
        {certificates.length === 0 ? (
          <InlineEmpty icon={<Award />} title={t('student360.results.noCertificates')} />
        ) : (
          <ul className="s360-list">
            {certificates.map((certificate) => (
              <li key={certificate.id} className="s360-item">
                <div className="s360-item__main">
                  <span className="s360-item__title" title={certificate.courseTitle}>{certificate.courseTitle}</span>
                  {certificate.number && <span className="s360-item__sub s360-mono-id">{certificate.number}</span>}
                </div>
                <div className="s360-item__side">
                  {certificate.withDistinction && (
                    <StatusBadge tone="warning">{t('student360.results.distinction')}</StatusBadge>
                  )}
                  <StatusBadge tone={CERTIFICATE_TONE[certificate.status] || 'neutral'}>
                    {t(`student360.results.certificateStatus.${certificate.status}`)}
                  </StatusBadge>
                  <span className="s360-item__date">{formatDate(certificate.issuedAt, lang)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Group>
    </section>
  );
}

export default ResultsSection;
